import { useAccount } from "@/contexts/AccountContext";
import { useTranslation } from "react-i18next";
import { Eye, X, ArrowLeftRight } from "lucide-react";

function getInitials(name?: string | null, email?: string | null) {
  const src = (name || email || "?").trim();
  const parts = src.split(/[\s@._-]+/).filter(Boolean);
  return ((parts[0]?.[0] ?? "?") + (parts[1]?.[0] ?? "")).toUpperCase();
}

/**
 * Indicateur d'impersonation : bandeau sticky en haut + pastille flottante
 * en bas à droite (toujours visible) pour confirmer en permanence l'espace
 * actif.
 */
export default function ImpersonationBanner() {
  const { isOwnAccount, activeAccount, accounts, switchAccount } = useAccount();
  const { t } = useTranslation();

  if (isOwnAccount || !activeAccount) return null;

  const ownAccount = accounts.find((a) => a.is_owner);
  const label = activeAccount.display_name || activeAccount.email || "—";
  const initials = getInitials(activeAccount.display_name, activeAccount.email);
  const readOnly = activeAccount.chat_mode === "read_only";

  return (
    <>
      {/* Bandeau haut sticky, plus visible (gradient + avatar) */}
      <div className="bg-gradient-to-r from-primary/15 via-primary/10 to-primary/15 border-b border-primary/30 text-foreground px-3 sm:px-4 py-2 text-xs sm:text-sm flex items-center justify-center gap-2 sm:gap-3 sticky top-0 z-40 backdrop-blur supports-[backdrop-filter]:bg-primary/10">
        <Eye className="h-4 w-4 text-primary shrink-0" />
        <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-primary text-primary-foreground text-[13px] font-bold shrink-0">
          {initials}
        </span>
        <span className="truncate">
          {t("workspace.banner", "Vous travaillez dans le compte de")}{" "}
          <strong className="text-primary">{label}</strong>
          <span className="hidden sm:inline">
            {" — "}
            {readOnly
              ? t("workspace.bannerReadOnly", "mode consultation")
              : t("workspace.bannerActions", "actions autorisées")}
            {" · "}
            <span title="Toutes les actions (chat, emails, uploads, recherches, génération) sont comptabilisées sur le compte du propriétaire, pas sur le vôtre.">
              quotas facturés à <strong className="text-primary">{label}</strong>
            </span>
          </span>
        </span>
        {ownAccount && (
          <button
            onClick={() => switchAccount(ownAccount.account_id)}
            className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-primary text-primary-foreground hover:bg-primary/90 transition-colors text-[13px] font-medium shrink-0"
          >
            <ArrowLeftRight className="h-3 w-3" />
            <span className="hidden sm:inline">{t("workspace.backToMine", "Revenir au mien")}</span>
            <span className="sm:hidden">{t("workspace.backShort", "Mon compte")}</span>
          </button>
        )}
      </div>

      {/* Pastille flottante toujours visible (bas-droite) */}
      <div className="fixed bottom-3 right-3 z-50 pointer-events-none sm:bottom-4 sm:right-4">
        <div className="pointer-events-auto group flex items-center gap-2 pl-1.5 pr-2 py-1 rounded-full bg-primary text-primary-foreground shadow-lg ring-2 ring-background text-xs font-medium">
          <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-primary-foreground/20 text-[13px] font-bold">
            {initials}
          </span>
          <span className="max-w-[140px] truncate" title={label}>
            {label}
          </span>
          {ownAccount && (
            <button
              onClick={() => switchAccount(ownAccount.account_id)}
              title={t("workspace.backToMine", "Revenir à mon compte") as string}
              aria-label={t("workspace.backToMine", "Revenir à mon compte") as string}
              className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-primary-foreground/20 hover:bg-primary-foreground/30 transition-colors"
            >
              <X className="w-3 h-3" />
            </button>
          )}
        </div>
      </div>
    </>
  );
}
